/**
 * Guarded Web Storage access.
 *
 * localStorage/sessionStorage throw in private browsing, with site data
 * blocked, and in some embedded webviews. Nothing in this app may fail to boot
 * because a preference could not be remembered, so every access goes through
 * here and degrades to "not stored".
 */

type Area = 'local' | 'session';

function area(kind: Area): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStorage(key: string, kind: Area = 'local'): string | null {
  try {
    return area(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: string, kind: Area = 'local'): void {
  try {
    area(kind)?.setItem(key, value);
  } catch {
    // Quota exceeded or storage blocked: the value lives for this page only.
  }
}

export function removeStorage(key: string, kind: Area = 'local'): void {
  try {
    area(kind)?.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
}

export function readJson<T>(key: string, kind: Area = 'local'): T | null {
  const raw = readStorage(key, kind);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown, kind: Area = 'local'): void {
  writeStorage(key, JSON.stringify(value), kind);
}
